import { Box, Flex, Text } from '@chakra-ui/react';
import {
  ChevronDownOutline,
  DropdownBase,
  DropdownOption,
  GroupTreeNode,
} from '@aslanpay/portal-common-lib';
import React, { ReactNode, useCallback, useMemo, useState } from 'react';

interface PridesTreeSelectProps {
  tree: GroupTreeNode[];
  selected: string[];
  onChange: (ids: string[]) => void;
}

function flattenTree(
  nodes: GroupTreeNode[],
  expandedIds: Set<string>,
  depth = 0
): DropdownOption[] {
  return nodes.flatMap((node) => {
    const hasChildren = (node.children?.length ?? 0) > 0;
    const option: DropdownOption = {
      value: node.id,
      text: node.name,
      depth,
      hasChildren,
      isExpanded: expandedIds.has(node.id),
    } as DropdownOption & { depth: number; hasChildren: boolean; isExpanded: boolean };

    const childOptions =
      hasChildren && expandedIds.has(node.id)
        ? flattenTree(node.children ?? [], expandedIds, depth + 1)
        : [];

    return [option, ...childOptions];
  });
}

function collectDescendantIds(node: GroupTreeNode): string[] {
  return [node.id, ...(node.children ?? []).flatMap(collectDescendantIds)];
}

function buildNodeMap(
  nodes: GroupTreeNode[],
  map = new Map<string, GroupTreeNode>()
): Map<string, GroupTreeNode> {
  nodes.forEach((node) => {
    map.set(node.id, node);
    if (node.children) buildNodeMap(node.children, map);
  });
  return map;
}

function PridesTreeSelect({ tree, selected, onChange }: PridesTreeSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const nodeMap = useMemo(() => buildNodeMap(tree), [tree]);
  const options = useMemo(() => flattenTree(tree, expandedIds), [tree, expandedIds]);

  const handleSelect = (value: string | number) => {
    const node = nodeMap.get(String(value));
    if (!node) return;
    const affected = collectDescendantIds(node);
    const allSelected = affected.every((id) => selected.includes(id));
    onChange(
      allSelected
        ? selected.filter((id) => !affected.includes(id))
        : [...new Set([...selected, ...affected])]
    );
  };

  const renderText = useCallback((option: DropdownOption): ReactNode => {
    const {
      depth = 0,
      hasChildren,
      isExpanded,
      value,
    } = option as DropdownOption & {
      depth: number;
      hasChildren: boolean;
      isExpanded: boolean;
    };

    return (
      <Flex align="center" w="full" pl={`${depth * 16}px`}>
        <Text textStyle="smallRegular" flex="1">
          {option.text}
        </Text>
        {hasChildren && (
          <Box
            as="span"
            ml="6px"
            flexShrink={0}
            cursor="pointer"
            transform={isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}
            transition="transform 0.2s"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              setExpandedIds((prev) => {
                const next = new Set(prev);
                if (next.has(String(value))) {
                  next.delete(String(value));
                } else {
                  next.add(String(value));
                }
                return next;
              });
            }}
          >
            <ChevronDownOutline boxSize="14px" color="brand.darkGray" />
          </Box>
        )}
      </Flex>
    );
  }, []);

  const displayValue = selected.length === 0 ? '' : `${selected.length} selected`;

  return (
    <Box w="full">
      <DropdownBase
        displayValue={displayValue}
        isMultiSelect
        isOpen={isOpen}
        isSearchable
        listMaxH="300px"
        onSelect={handleSelect}
        onSelectAll={(allValues) => onChange(allValues.map(String))}
        onToggle={() => setIsOpen((prev) => !prev)}
        options={options}
        placeholder="All prides"
        renderText={renderText}
        selected={selected}
        isSelectAllEnabled
        selectAllDisplayLabel="All prides"
      />
    </Box>
  );
}

export default PridesTreeSelect;
